import {
  Alert,
  Box,
  Button,
  DialogActions,
  MenuItem,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import { ReactElement, useState } from "react";
import * as Yup from "yup";

import {
  ActBlueWebhookCreationReturnType,
  ActBlueWebhookIntegration,
  ActBlueWebhookSubscribeDonorsOptionsType,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import ReadOnlyClickToCopyList from "~frontend/components/ReadOnlyClickToCopyList";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import { mediaValidationSchema } from "~frontend/components/forms/FormikMediaField";
import FormikSwitchField from "~frontend/components/forms/FormikSwitchField";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import {
  CREATE_ACT_BLUE_WEBHOOK_INTEGRATION,
  LIST_INTEGRATIONS,
  UPDATE_ACT_BLUE_WEBHOOK_INTEGRATION,
} from "~frontend/graphql/integration";
import { GET_SUBACCOUNT_SHORTCODE } from "~frontend/graphql/shortcode";
import { useMediaUpload } from "~frontend/hooks/useMediaUpload";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import { ScriptEditor } from "../campaignEditor/Script/ScriptEditor";

export function ActBlueWebhookIntegrationSettingsForm({
  integration,
  subaccountId,
  afterCreate,
  onClose,
}: {
  integration: ActBlueWebhookIntegration | null;
  subaccountId: string;
  afterCreate?: () => void;
  onClose: () => void;
}): ReactElement {
  const editing = !!integration;
  const subaccount = useSubaccountAdminConfig()!;

  const shortcodeEnabled =
    !!subaccount.shortcode && subaccount.shortcode.state === "ACTIVE";

  const [oneTimeData, setOneTimeData] =
    useState<ActBlueWebhookCreationReturnType | null>(null);

  const [createActBlueWebhookIntegration] = useMutation(
    CREATE_ACT_BLUE_WEBHOOK_INTEGRATION,
    {
      refetchQueries: [LIST_INTEGRATIONS, GET_SUBACCOUNT_SHORTCODE],
      awaitRefetchQueries: true,
    }
  );

  const [updateActBlueWebhookIntegration] = useMutation(
    UPDATE_ACT_BLUE_WEBHOOK_INTEGRATION,
    {
      refetchQueries: [LIST_INTEGRATIONS, GET_SUBACCOUNT_SHORTCODE],
      awaitRefetchQueries: true,
    }
  );

  const media = integration?.settings?.sendDonationMessage?.mediaUpload
    ? {
        upload: integration.settings.sendDonationMessage.mediaUpload,
      }
    : null;

  const initialValues = {
    name: integration?.name || "",
    username: integration?.id || "",
    webhookUrl: integration?.settings?.webhookUrl || "",
    subscribeDonors: integration?.settings?.subscribeDonors || "",
    sendDonationMessageToggle:
      !!integration?.settings?.sendDonationMessage?.script,
    sendDonationMessage: {
      script: integration?.settings?.sendDonationMessage?.script || "",
      media,
    },
  };

  const subscribeDonorsLabels = {
    [ActBlueWebhookSubscribeDonorsOptionsType.OptIns]: "Explicit Opt Ins",
    [ActBlueWebhookSubscribeDonorsOptionsType.OptInsAndUnknown]:
      "Explicit Opt Ins and Unknowns",
  };

  const { uploadMedia } = useMediaUpload();
  const getErrorMessage = useErrorMessageFormatter();

  return (
    <Formik
      initialValues={{
        [submitErrorField]: null,
        ...initialValues,
      }}
      validationSchema={Yup.object({
        name: Yup.string().required("Required"),
        subscribeDonors: Yup.string(),
        sendDonationMessageToggle: Yup.boolean(),
        sendDonationMessage: Yup.object({
          script: Yup.string(),
          media: mediaValidationSchema(),
        }),
      })}
      onSubmit={async (values, { setErrors }) => {
        const {
          name,
          subscribeDonors,
          sendDonationMessageToggle,
          sendDonationMessage,
        } = values;

        const mediaUpload = await uploadMedia(sendDonationMessage?.media);

        const input = {
          name,
          subscribeDonors: subscribeDonors || null,
          sendDonationMessage:
            subscribeDonors && sendDonationMessageToggle
              ? {
                  script: sendDonationMessage?.script,
                  mediaUploadId: mediaUpload?.id || null,
                }
              : null,
        };

        const { errors, data } = editing
          ? await updateActBlueWebhookIntegration({
              variables: {
                input: {
                  ...input,
                  integrationId: integration.id,
                },
              },
            })
          : await createActBlueWebhookIntegration({
              variables: {
                input: {
                  ...input,
                  subaccountId,
                },
              },
            });

        if (errors) {
          setErrors({
            [submitErrorField]: getErrorMessage(errors),
          });
        }

        setOneTimeData(data.createActBlueWebhookIntegration);
        if (afterCreate) {
          afterCreate();
        } else {
          onClose();
        }
      }}
    >
      {({ isSubmitting, values }) => {
        return (
          <Form>
            <FormikErrorBanner />
            <FormikTextField
              name="name"
              label="Name Your Integration"
              disabled={isSubmitting || !!oneTimeData}
              fullWidth
            />
            {shortcodeEnabled && (
              <>
                <FormikSelect
                  name="subscribeDonors"
                  label="Subscribe donors to your short code?"
                  disabled={isSubmitting || !!oneTimeData}
                  withNoneOption={true}
                  noneOptionLabel="No"
                  fullWidth
                >
                  {Object.entries(subscribeDonorsLabels).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </FormikSelect>

                {(values.subscribeDonors ===
                  ActBlueWebhookSubscribeDonorsOptionsType.OptIns ||
                  values.subscribeDonors ===
                    ActBlueWebhookSubscribeDonorsOptionsType.OptInsAndUnknown) && (
                  <>
                    <FormikSwitchField
                      name="sendDonationMessageToggle"
                      label="Send donation thank you message"
                    />
                    {values.sendDonationMessageToggle && (
                      <FlexBox flexDirection="column" gap={2}>
                        <Typography variant="body2">
                          This message will be sent alongside the standard
                          opt-in welcome message if your contact is not
                          subscribed to your opt-in list. Subscribed contacts
                          will only receive your thank you message.
                        </Typography>
                        <Box sx={{ p: 2, backgroundColor: palette.lightGray }}>
                          <ScriptEditor
                            type="donation"
                            fieldPrefix="sendDonationMessage"
                            script={values.sendDonationMessage?.script}
                            media={values.sendDonationMessage?.media}
                            hideContactFields
                            hidePreview
                          />
                        </Box>
                      </FlexBox>
                    )}
                  </>
                )}
              </>
            )}
            {editing && (
              <>
                <ReadOnlyClickToCopyList
                  listData={[
                    { label: "Username", value: values.username },
                    { label: "Webhook URL", value: values.webhookUrl },
                  ]}
                />
                <Alert severity="info">
                  Your webhook password is only displayed once, right after you
                  create your ActBlue integration. If you've lost your webhook
                  password, you'll need to create a new ActBlue Webhook
                  integration.
                </Alert>
              </>
            )}

            <Box sx={{ pb: 2 }} />

            {oneTimeData ? (
              <ActBlueWebhookIntegrationOneTimeView
                oneTimeViewData={oneTimeData}
              />
            ) : (
              <DialogActions
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                {!editing ? (
                  <Typography variant="body2">
                    Your username, password & webhook url will be generated once
                    the integration is created.
                  </Typography>
                ) : (
                  <div />
                )}
                <FlexBox gap={1}>
                  <Button
                    disabled={isSubmitting}
                    onClick={() => onClose()}
                    color="secondary"
                  >
                    Cancel
                  </Button>
                  <FormikSubmitButton
                    color="primary"
                    variant="contained"
                    submitText={editing ? "Save" : "Create"}
                    loadingText={editing ? "Saving..." : "Creating..."}
                  />
                </FlexBox>
              </DialogActions>
            )}
          </Form>
        );
      }}
    </Formik>
  );
}

function ActBlueWebhookIntegrationOneTimeView({
  oneTimeViewData,
}: {
  oneTimeViewData: ActBlueWebhookCreationReturnType | null;
}): ReactElement {
  return (
    <>
      <Alert severity="warning">
        Keep this password in a safe place. You won't be able to view it again.
      </Alert>
      <ReadOnlyClickToCopyList
        listData={[
          { label: "Endpoint URL", value: oneTimeViewData?.webhookUrl ?? null },
          { label: "Username", value: oneTimeViewData?.username ?? null },
          { label: "Password", value: oneTimeViewData?.password ?? null },
        ]}
      />
    </>
  );
}
